import React, { createContext, useReducer, useContext } from "react";
import { computePages, getNewPage, initialWidgets } from "./utils";
import reducer from "./reducer";
import {
  NotificationContext,
  useErrorNotification,
} from "@common/components/Notification";
import { getWidgets, postWidgetsScreens } from "@services/widgets";
import { useTranslation } from "react-i18next";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import {
  canViewDevice,
  canViewMeeting,
  canViewRoom,
  canViewTicket,
} from "@common/utils/user";
import { useUserSetting } from "@common/components/ProfileBadge/DrawerContent/SettingsContent";

export const CAROUSEL_STORAGE_KEY = "enable_carousel";
const defaultContext: WidgetContextEntity = {
  pages: [],
  editMode: false,
  currentPage: 0,
  dispatch: () => undefined,
  isDragging: false,
  previousPages: [],
  widgetHeight: 0,
  fontSize: 0,
  canCarousel: true,
  isCarouselEnabled: true,
  fetch: () => {},
};

export const WidgetContext = createContext<WidgetContextEntity>(defaultContext);

const Provider = (props: React.PropsWithChildren<{}>) => {
  const { dispatchNotification } = useContext(NotificationContext);
  const [user] = useAuthenticatedUser();
  const dispatchError = useErrorNotification();
  const { t } = useTranslation();
  const [isCarouselEnabled] = useUserSetting("isCarouselActive");
  const wrapper: typeof reducer = (state, action) => {
    const newState = reducer(state, action);
    const pages = computePages(newState.pages).filter(
      (page) => page.widgets.length > 0
    );
    let currentPage = Math.min(
      newState.currentPage,
      Math.max(0, pages.length - 1)
    );
    return {
      ...newState,
      pages,
      previousPages: [...state.pages],
      currentPage,
    };
  };

  const [state, dispatch] = useReducer(wrapper, {
    ...defaultContext,
    isCarouselEnabled,
  });
  const fetch = async (userId: string) => {
    try {
      let pages = await getWidgets(userId);
      if (pages.length === 0) {
        const { devices, meetings, tickets, rooms } = {
          devices: canViewDevice(user),
          meetings: canViewMeeting(user),
          tickets: canViewTicket(user),
          rooms: canViewRoom(user),
        };
        if ([devices, meetings, tickets, rooms].every((v) => !v)) {
          dispatchNotification({
            title: t("Initialization"),
            severity: "info",
            message: t("Your account cannot have widgets."),
          });
          return;
        }
        dispatchNotification({
          title: t("Initialization"),
          severity: "info",
          message: t("Creating your first widgets."),
        });
        let newPage: any = getNewPage();
        newPage.widgets = initialWidgets({ devices, meetings, tickets, rooms });
        delete newPage.userWidgetScreenId;
        await postWidgetsScreens(userId, [newPage]);
        pages = await getWidgets(userId);
      }
      dispatch({ type: "updatePages", data: pages });
    } catch (e) {
      dispatchError(e);
    }
  };

  return (
    <WidgetContext.Provider
      value={{
        ...state,
        dispatch,
        fetch,
        fontSize: state.widgetHeight / 6,
        isCarouselEnabled,
      }}
    >
      {props.children}
    </WidgetContext.Provider>
  );
};
export default Provider;
